/**
 * Lookup-or-generate cache keyed by a scopes-array reference.
 *
 * Callers pass `(scopes, generator)`; the helper returns the cached
 * value if the same scopes-array reference has been seen before with
 * the same generator, otherwise it invokes `generator(scopes)`, caches
 * the result, and returns it.
 *
 * Cache keys:
 *   - outer: the generator function's reference identity (so consumers
 *     using different generators never collide on the same scopes array)
 *   - inner: the scopes array's reference identity (`WeakMap`), so the
 *     entry is GC'd when the caller drops its reference to the array
 *
 * Callers that pass a stable scopes reference (e.g. straight from a
 * `useScopes()` selector) hit the cache after the first call; callers
 * that rebuild the array each render miss every time and pay the full
 * cost.
 */
const caches = new WeakMap();
export const getCachedMap = (scopes, generator) => {
  let cache = caches.get(generator);
  if (!cache) {
    cache = new WeakMap();
    caches.set(generator, cache);
  }
  if (cache.has(scopes)) {
    return cache.get(scopes);
  }
  const result = generator(scopes);
  cache.set(scopes, result);
  return result;
};